import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { ChevronLeft } from 'lucide-react';

import { OutlookMailboxSettings } from './outlook-mailbox-settings';
import { SettingsCard } from './settings-card';
import { useSettingsPanelContext } from './settings-panel-context';
import { MSGraphToggle } from '../../ms-graph';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useMSGraphAuth } from '../contexts/ms-graph-context';
import { useChatSettings } from '../hooks/use-chat-settings';

function OutlookManageView({ onBack }: { onBack: () => void }) {
  const msGraphAuth = useMSGraphAuth();
  const graphClient = msGraphAuth?.graphClient ?? null;
  const { serverSettings, persistSetting } = useChatOverlayContext();

  const chatSettings = useChatSettings({
    graphClient,
    serverSelectedGroupIds: serverSettings?.outlookConnectedEmailGroupIds,
    serverSharedInboxes: serverSettings?.outlookSharedInboxes,
    onPersistSelectedGroups: (groupIds) => persistSetting('outlookConnectedEmailGroupIds', groupIds),
    onPersistSharedInboxes: (emails) => persistSetting('outlookSharedInboxes', emails),
  });
  return (
    <div className="flex flex-col gap-2">
      <button type="button" onClick={onBack} className="text-primary flex items-center gap-1 text-sm font-medium">
        <ChevronLeft className="h-4 w-4" />
        Back to settings
      </button>
      <div className="bg-background border-border flex flex-col rounded-sm border px-6 py-2">
        <OutlookMailboxSettings {...chatSettings} />
      </div>
    </div>
  );
}

/**
 * Outlook Connect settings component that displays a toggle switch for enabling/disabling Outlook Connect.
 * Uses MSGraphToggle primitive to manage Outlook Connect state.
 * When connected, shows a "Manage" button that pushes a full-panel sub-view via SettingsPanelContext.
 */
export function OutlookConnectSettings() {
  const isMSGraphAuthEnabled = useFeatureFlag('nova-tools-graph-api', false);
  const { outlookConnectEnabled } = useChatOverlayContext();
  const { pushView, popView } = useSettingsPanelContext();

  // Don't render if feature flag is disabled
  if (!isMSGraphAuthEnabled) {
    return null;
  }

  return (
    <SettingsCard title="Outlook Connect" action={<MSGraphToggle />}>
      <p className="text-muted-foreground text-sm">Allow Nova to access your Outlook inbox</p>

      {/* Manage row — shown only when connected */}
      {outlookConnectEnabled && (
        <div className="bg-muted/75 mt-2 flex items-center justify-between rounded-md px-4 py-3 text-sm">
          <span className="font-semibold">Email Groups and Shared Inboxes</span>
          <button
            type="button"
            onClick={() => pushView(<OutlookManageView onBack={popView} />)}
            className="text-primary font-medium"
          >
            Manage
          </button>
        </div>
      )}
    </SettingsCard>
  );
}
